import React, { useMemo, useRef, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import './Users.css';
import '../../components/ClearFilter.css';
import Pagination from '../../components/Pagination';
import { apiFetch } from '../../utils/api';

const ClearFilter = ({ onClear }) => {
  return (
    <button onClick={onClear} className="clear-filter-btn">
      Clear Filters
    </button>
  );
};

const Snackbar = ({ message, type, onClose }) => {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
      if (onClose) onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  if (!visible) return null;

  const bgColor = type === 'error' ? '#ef4444' : '#166534';
  return (
    <div className="mlpu-snackbar" style={{ backgroundColor: bgColor }}>
      {message}
    </div>
  );
};

const Users = () => {
  const { name } = useParams();
  const learningPathId = name.split("-").pop();
  const learningPathSlug = name.replace(`-${learningPathId}`, "");

  console.log("Learning Path ID:", learningPathId);
  console.log("Learning Path Slug:", learningPathSlug);

  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ total_users: 0, assigned: 0, completed: 0, avg_progress: 0 });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [departmentFilter, setDepartmentFilter] = useState('');
  const headerCbRef = useRef(null);
  const allSelected = selectedIds.size === users.length && users.length > 0;
  const isIndeterminate = selectedIds.size > 0 && selectedIds.size < users.length;
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ message: '', type: '', open: false });

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [usersData, statsData] = await Promise.all([
          apiFetch(`${window._env_.API_BASE_URL}/learning_paths/${learningPathId}/users`),
          apiFetch(`${window._env_.API_BASE_URL}/learning_paths/${learningPathId}/assignment-stats`)
        ]);
        setUsers(usersData || []);
        setStats(statsData || { total_users: 0, assigned: 0, completed: 0, avg_progress: 0 });
        const assignedIds = usersData.filter(u => u.is_assigned).map(u => u.id);
        setSelectedIds(new Set(assignedIds));
      } catch (error) {
        console.error('Error fetching data:', error);
        setUsers([]);
        setStats({ total_users: 0, assigned: 0, completed: 0, avg_progress: 0 });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [learningPathId]);

  useEffect(() => {
    if (headerCbRef.current) {
      headerCbRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const toggleAll = (checked) => {
    if (checked) {
      setSelectedIds(new Set(users.map(u => u.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    const previousSelectedIds = new Set(selectedIds); // Store current state

    try {
      const assignerEmail = localStorage.getItem('userEmail');
      if (!assignerEmail) throw new Error('No user email found in localStorage');

      const response = await apiFetch(
        `${window._env_.API_BASE_URL}/learning_paths/${learningPathId}/assign-users`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_ids: Array.from(selectedIds),
            assigned_by: assignerEmail,
          }),
        }
      );

      const [usersData, statsData] = await Promise.all([
        apiFetch(`${window._env_.API_BASE_URL}/learning_paths/${learningPathId}/users`),
        apiFetch(`${window._env_.API_BASE_URL}/learning_paths/${learningPathId}/assignment-stats`)
      ]);

      setUsers(usersData || []);
      setSelectedIds(new Set(usersData.filter(u => u.is_assigned).map(u => u.id)));
      setStats(statsData || { total_users: 0, assigned: 0, completed: 0, avg_progress: 0 });

      setSnackbar({ message: 'Assignments saved successfully', type: 'success', open: true });
    } catch (error) {
      console.error('Error saving changes:', error);
      setSelectedIds(previousSelectedIds); // Revert to previous state on error
      setSnackbar({
        message: error.message || 'An error occurred while saving',
        type: 'error',
        open: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getProgressBarColor = (progress, status) => {
    if (status === 'completed') return '#10B981';
    if (progress > 50) return '#3B82F6';
    return '#D1D5DB';
  };

  const getStatusBadge = (deptOrStatus) => {
    return (
      <span
        className="mlpu-status-badge"
        style={{ color: '#374151', backgroundColor: '#F3F4F6' }}
      >
        {deptOrStatus}
      </span>
    );
  };

  const filteredUsers = useMemo(() => {
    let filtered = users;
    if (departmentFilter) {
      filtered = filtered.filter(u => u.department === departmentFilter);
    }
    const q = search.trim().toLowerCase();
    return q ? filtered.filter(u =>
      u.name.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q)
    ) : filtered;
  }, [users, search, departmentFilter]);

  const handleClearFilters = () => {
    setSearch('');
    setDepartmentFilter('');
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentUsers = Array.isArray(filteredUsers) ? filteredUsers.slice(indexOfFirstItem, indexOfLastItem) : [];

  const uniqueDepartments = [...new Set(users.map(u => u.department))];

  return (
    <div className="mlpu-user-assignment-container">
      <div className="mlpu-stats-container">
        <div className="mlpu-stat-item">
          <div className="mlpu-stat-icon mlpu-users-icon">👥</div>
          <div className="mlpu-stat-info">
            <span className="mlpu-stat-number">{stats.total_users}</span>
            <span className="mlpu-stat-label">Total Users</span>
          </div>
        </div>
        <div className="mlpu-stat-item">
          <div className="mlpu-stat-icon mlpu-assigned-icon">✅</div>
          <div className="mlpu-stat-info">
            <span className="mlpu-stat-number">{stats.assigned}</span>
            <span className="mlpu-stat-label">Assigned</span>
          </div>
        </div>
        <div className="mlpu-stat-item">
          <div className="mlpu-stat-icon mlpu-completed-icon">🏆</div>
          <div className="mlpu-stat-info">
            <span className="mlpu-stat-number">{stats.completed}</span>
            <span className="mlpu-stat-label">Completed</span>
          </div>
        </div>
        <div className="mlpu-stat-item">
          <div className="mlpu-stat-icon mlpu-avg-progress-icon">📈</div>
          <div className="mlpu-stat-info">
            <span className="mlpu-stat-number">{stats.avg_progress}%</span>
            <span className="mlpu-stat-label">Avg Progress</span>
          </div>
        </div>
      </div>
      <div className="mlpu-ua-toolbar">
        <div className="mlpu-ua-left">
          <h2 className="mlpu-ua-title">User Assignment</h2>
          <button type="button" className="mlpu-ua-assigned-link">
            {selectedIds.size} of {users.length} users assigned
          </button>
        </div>
        <div className="mlpu-ua-right">
          <div className="mlpu-ua-search">
            <span className="mlpu-ua-search-icon">🔍</span>
            <input
              type="text"
              className="mlpu-ua-search-input"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="mlpu-ua-search-input"
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            style={{ padding: '10px', marginRight: '12px' }}
          >
            <option value="">All Departments</option>
            {uniqueDepartments.map((dept, index) => (
              <option key={index} value={dept}>{dept}</option>
            ))}
          </select>
          <ClearFilter onClear={handleClearFilters} />
          <button
            className="mlpu-ua-save-btn"
            onClick={handleSaveChanges}
            disabled={isSaving || isLoading}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
      <div className="mlpu-table-wrapper">
        {(isLoading || isSaving) && (
          <div className="mlpu-overlay">
            <div className="mlpu-spinner"></div>
            <p className="mlpu-overlay-text">
              {isSaving ? "Saving changes..." : "Loading..."}
            </p>
          </div>
        )}
        <table className="mlpu-users-table">
          <thead>
            <tr>
              <th className="mlpu-checkbox-col">
                <input
                  ref={headerCbRef}
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => toggleAll(e.target.checked)}
                  disabled={isSaving || isLoading}
                />
              </th>
              <th>USER</th>
              <th>EMAIL</th>
              <th>DEPARTMENT</th>
              <th>PROGRESS</th>
            </tr>
          </thead>
          <tbody>
            {!isLoading && !isSaving && currentUsers.length === 0 ? (
              <tr>
                <td colSpan="5" className="mlpu-no-records">
                  No Records Found
                </td>
              </tr>
            ) : (
              currentUsers.map((user) => (
                <tr key={user.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(user.id)}
                      onChange={() => toggleOne(user.id)}
                      disabled={isSaving || isLoading}
                    />
                  </td>
                  <td className="mlpu-user-cell">{user.name}</td>
                  <td className="mlpu-email-cell">{user.email}</td>
                  <td className="mlpu-department-cell">{getStatusBadge(user.department)}</td>
                  <td className="mlpu-progress-cell">
                    {user.is_assigned ? (
                      <div className="mlpu-progress-container">
                        <div className="mlpu-progress-bar">
                          <div
                            className="mlpu-progress-fill"
                            style={{
                              width: `${user.progress || 0}%`,
                              backgroundColor: getProgressBarColor(user.progress || 0, user.status),
                            }}
                          />
                        </div>
                        <span className="mlpu-progress-text">{`${user.progress || 0}%`}</span>
                      </div>
                    ) : (
                      <div className="mlpu-locked-progress" title="Progress locked for unassigned users">
                        <span className="mlpu-lock-icon">🔒</span>
                        <span className="mlpu-locked-text">Locked</span>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Pagination
        items={filteredUsers}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      />
      {snackbar.open && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={handleSnackbarClose}
        />
      )}
    </div>
  );
};

export default Users;